import FrameMessenger from 'frame-messenger';
import Browser from 'browser';
import Logger from './utils/logger';
import ENV from '../config/env';
import TrackingState from './tracking-state';

import { ANALYTICS_VARIABLE_KEY_NAME } from '../config/constants';

export default class GWITracker {
  static initializeSDK(apiKey, options = {}) {
    const analyticsLocation = options.analyticsLocation || `${ENV.analyticsHost}/analytics.js`;
    const analyticsVariableKeyName = options.analyticsVariableKeyName || '_gwi_analytics_brick_internal_function';
    const analyticsGWIHost = options.analyticsGWIHost || ENV.analyticsGWIHost;
    const browserWindow = Browser.getWindow();
    const browserDocument = Browser.getDocument();

    if (!browserWindow[ANALYTICS_VARIABLE_KEY_NAME]) {
      // Bootstrap gwi function, so even if not allowed, the calls do not blow up.
      browserWindow[ANALYTICS_VARIABLE_KEY_NAME] = analyticsVariableKeyName;
    } else {
      Logger.debug('analytics is already set up under the function name', browserWindow[ANALYTICS_VARIABLE_KEY_NAME]);
      return;
    }

    const key = browserWindow[ANALYTICS_VARIABLE_KEY_NAME];

    browserWindow[key] = browserWindow[key] || function push(...args) {
      (browserWindow[key].q = browserWindow[key].q || []).push(args);
    };

    const analyticsScriptTag = browserDocument.createElement('script');
    const firstScriptTag = browserDocument.getElementsByTagName('script')[0];

    analyticsScriptTag.async = 1;
    analyticsScriptTag.src = analyticsLocation;
    firstScriptTag.parentNode.insertBefore(analyticsScriptTag, firstScriptTag);

    if (ENV.debug) {
      GWITracker.analytics('configure', { gwiHost: analyticsGWIHost, debug: true });
    }

    GWITracker.analytics('create', apiKey);
  }

  static trackTrailerPlay(trackingState, time, options = {}) {
    const parameters = {
      time,
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'trailer_play', parameters);
  }

  static trackTrailerPause(trackingState, time, options = {}) {
    const parameters = {
      time,
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'trailer_pause', parameters);
  }

  static trackTrailerEnd(trackingState, time, options = {}) {
    const parameters = {
      time,
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'trailer_end', parameters);
  }

  static trackTheatricalRender(trackingState, term, options = {}) {
    const parameters = {
      term,
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'theatrical_render', parameters);
  }

  static trackScheduleRender(trackingState, term, options = {}) {
    const parameters = {
      term,
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'schedule_render', parameters);
  }

  static trackBrickImpression(trackingState, options = {}) {
    const parameters = {
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'brick_impression', parameters);
  }

  static trackImpression(trackingState, options = {}) {
    const parameters = {
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'impression', parameters);
  }

  static trackBrickHover(trackingState, options = {}) {
    const parameters = {
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'brick_hover', parameters);
  }

  static trackExternalLinkClick(trackingState, options = {}) {
    const parameters = {
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'external_link_click', parameters);
  }

  static trackCallToActionClick(trackingState, options = {}) {
    const parameters = {
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'cta_click', parameters);
  }

  static trackListItemClick(trackingState, options = {}) {
    const contextType = trackingState.listItemContextType;
    const contextId = trackingState.listItemContextId;

    const parameters = {
      contextType,
      contextId,
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'list_item_click', parameters);
  }

  static trackAddToQueue(trackingState, options = {}) {
    const parameters = {
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'add_to_queue', parameters);
  }

  static trackRemoveFromQueue(trackingState, options = {}) {
    const parameters = {
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'remove_from_queue', parameters);
  }

  static trackAddToList(trackingState, options = {}) {
    const parameters = {
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'add_to_list', parameters);
  }

  static trackRemoveFromList(trackingState, options = {}) {
    const parameters = {
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'remove_from_list', parameters);
  }

  static trackUnRate(trackingState, options = {}) {
    const parameters = {
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'unrate', parameters);
  }

  static trackRate(trackingState, rating, options = {}) {
    const parameters = {
      rating,
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'rate', parameters);
  }

  static trackProviderClick(trackingState, providerFormatId, providerFormatName, options = {}) {
    const parameters = {
      providerFormatId,
      providerFormatName,
      ...options,
    };

    GWITracker.fireTransaction(trackingState, 'provider_click', parameters);
  }

  static fireTransaction(trackingState, event, options) {
    let trackingOptions;

    if (trackingState instanceof TrackingState) {
      trackingOptions = trackingState.toTrackingOptions();
    } else {
      trackingOptions = trackingState;
    }

    const parameters = {
      ...trackingOptions,
      ...options,
    };
    const data = {
      event,
      parameters,
    };

    GWITracker.sendFireTrackingEvent(trackingState, data);
  }

  static sendFireTrackingEvent(trackingState, options) {
    GWITracker.send(trackingState, 'fire-tracking-event', options);
  }

  static send(trackingState, event, data) {
    const enrichedData = {
      ...data,
      brickId: trackingState.brickId,
    };

    if (trackingState.inWidget) {
      FrameMessenger.send(Browser.getWindow(), event, enrichedData, '*');
    } else {
      FrameMessenger.sendToParent(event, enrichedData);
    }
  }

  static getGWIAnalyticsFunction() {
    const browserWindow = Browser.getWindow();
    return browserWindow[browserWindow[ANALYTICS_VARIABLE_KEY_NAME]];
  }

  static analytics(action, eventOrOptions, options) {
    const gwi = GWITracker.getGWIAnalyticsFunction();

    gwi(action, eventOrOptions, options);
  }

  static fireAnalyticsTransaction(event, options) {
    Logger.debug('Firing', event, 'with options', options);

    GWITracker.analytics('send', event, options);
  }
}



// WEBPACK FOOTER //
// ./~/gwi-tracker/src/gwi-tracker.js